// IM-EXT-002 IM-UI-031 — Nova análise. Parte do contexto confirmado: a pessoa escolhe ofertas e foco; o InteliMove
// sugere região e concorrentes conhecidos. Nada de explicar de novo quem é a empresa.
import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { AsyncView, EmptyState } from '../components/States';
import { PageHead } from '../components/Widgets';
import { ASPECTS, marketApi, TYPE_LABEL, type ContextOptions } from '../data/market';
import { useResource } from '../hooks/useResource';

export function MarketNew() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { state, reload } = useResource((s) => marketApi.options(s), []);
  // IM-EXT-013: descobrir clientes potenciais é a jornada "Encontrar empresas" (Empresas-alvo), não uma análise de mercado.
  if (params.get('tipo') === 'TARGET_DISCOVERY') return <Navigate to="/empresas-alvo?encontrar=1" replace />;
  return (
    <section aria-labelledby="page-title">
      <PageHead eyebrow="Inteligência de Mercado" title="Nova análise" subtitle="Escolha o que pesquisar. Quem é a sua empresa já vem do contexto confirmado." />
      <AsyncView state={state} onRetry={reload} empty={null}>
        {(o) => (o.ready ? <NewForm o={o} onCreated={(id) => nav(`/mercado/${id}`)} /> : (
          <EmptyState title="Confirme o contexto da empresa primeiro" glyph="lock" tone="amber"
            action={<Link className="cta" to="/configuracoes/contexto" style={{ display: 'inline-block', textDecoration: 'none' }}>Completar o contexto</Link>}>
            A análise usa as ofertas, ICPs, personas e mercados confirmados. Sem um contexto confirmado, a pesquisa não pode começar.
          </EmptyState>
        ))}
      </AsyncView>
    </section>
  );
}

function NewForm({ o, onCreated }: { o: ContextOptions; onCreated: (id: string) => void }) {
  const [offerings, setOfferings] = useState<string[]>(o.offerings.length === 1 ? [o.offerings[0]!.id] : []);
  const [name, setName] = useState('');
  const [objective, setObjective] = useState('');
  const [params] = useSearchParams();
  const [type, setType] = useState(params.get('tipo') && TYPE_LABEL[params.get('tipo')!] && params.get('tipo') !== 'TARGET_DISCOVERY' ? params.get('tipo')! : 'COMPETITIVE_LANDSCAPE');
  const [icpIds, setIcpIds] = useState<string[]>([]);
  const [region, setRegion] = useState(o.suggestedRegion ?? '');
  const [known, setKnown] = useState<string[]>(o.competitors.map((c) => c.id));
  const [extra, setExtra] = useState('');
  const [aspects, setAspects] = useState<string[]>(ASPECTS.filter((a) => a.main).map((a) => a.key));
  const [discoverNew, setDiscoverNew] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [more, setMore] = useState(false);

  // Sugestão proativa: nome e objetivo a partir das ofertas escolhidas (a pessoa pode reescrever).
  useEffect(() => {
    const names = o.offerings.filter((x) => offerings.includes(x.id)).map((x) => x.name);
    if (!names.length) return;
    setName((n) => (n && !n.startsWith('Concorrência em ') ? n : `Concorrência em ${names.slice(0, 2).join(' e ')}`.slice(0, 160)));
    setObjective((t) => (t && !t.startsWith('Entender quem disputa') ? t : `Entender quem disputa clientes de ${names.join(', ')}${region ? ` em ${region}` : ''}, como se posicionam e onde estão as oportunidades.`.slice(0, 1000)));
  }, [offerings, region, o.offerings]);

  const toggle = (list: string[], set: (v: string[]) => void, id: string) => set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!offerings.length) return setError('Selecione ao menos uma oferta do catálogo.');
    setBusy(true);
    try {
      const extraCompetitors = extra.split(/[\n,;]/).map((s) => s.trim()).filter((s) => s.length >= 2).slice(0, 15).map((n) => ({ name: n }));
      const d = await marketApi.create({
        name: name.trim(), objective: objective.trim(), offeringIds: offerings, analysisType: type, region: region.trim() || undefined,
        aspects, discoverNew, includeArchived, knownCompetitorIds: known, extraCompetitors, icpIds, notes: notes.trim() || undefined, contextSnapshotId: o.contextSnapshotId ?? undefined,
      });
      onCreated(d.id);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <form className="card market-form" onSubmit={submit} aria-describedby={error ? 'new-error' : undefined}>
      <p className="card-sub">Empresa: <b>{o.company}</b> · contexto confirmado versão {o.contextVersion}. ICPs: {o.icps.map((i) => i.name).join(', ') || '—'} · Personas: {o.personas.join(', ') || '—'}.</p>
      <fieldset className="field wide"><legend>O que você quer analisar? <span className="cell-sub">Ofertas do seu catálogo</span></legend>
        <div className="check-grid">
          {o.offerings.map((x) => (
            <label key={x.id} className="check"><input type="checkbox" checked={offerings.includes(x.id)} onChange={() => toggle(offerings, setOfferings, x.id)} /> {x.name}</label>
          ))}
        </div>
      </fieldset>
      <div className="form-grid">
        <label className="field"><span>Nome da análise</span><input required minLength={3} maxLength={160} value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label className="field"><span>Região ou mercado</span><input maxLength={200} value={region} placeholder="Ex.: Brasil, Sudeste, São Paulo" onChange={(e) => setRegion(e.target.value)} /></label>
        <label className="field wide"><span>Objetivo comercial</span><textarea required minLength={10} maxLength={1000} rows={3} value={objective} onChange={(e) => setObjective(e.target.value)} /></label>
        <label className="field"><span>Tipo de análise</span>
          <select value={type} onChange={(e) => setType(e.target.value)}>{Object.entries(TYPE_LABEL).filter(([k]) => k !== 'TARGET_DISCOVERY').map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
        <label className="field check-inline"><input type="checkbox" checked={discoverNew} onChange={(e) => setDiscoverNew(e.target.checked)} /> Descobrir também concorrentes que ainda não conheço</label>
        <label className="field check-inline"><input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} /> Incluir concorrentes arquivados <span className="cell-sub">Por padrão, empresas que você arquivou ou descartou ficam fora da análise.</span></label>
      </div>
      {['TARGET_DISCOVERY', 'POTENTIAL_MARKET'].includes(type) && o.icps.length > 0 && (
        <fieldset className="field wide"><legend>Cliente ideal (ICP) a usar na aderência</legend>
          <div className="check-grid">{o.icps.map((i) => <label key={i.id} className="check"><input type="checkbox" checked={icpIds.includes(i.id)} onChange={() => toggle(icpIds, setIcpIds, i.id)} /> {i.name}</label>)}</div>
          <p className="cell-sub">Sem seleção, a InteliMove usa os ICPs ligados às ofertas escolhidas.</p>
        </fieldset>
      )}
      {o.competitors.length > 0 && (
        <fieldset className="field wide"><legend>Concorrentes conhecidos (do contexto)</legend>
          <div className="check-grid">
            {o.competitors.map((c) => <label key={c.id} className="check"><input type="checkbox" checked={known.includes(c.id)} onChange={() => toggle(known, setKnown, c.id)} /> {c.name}{c.status === 'HYPOTHESIS' ? ' (hipótese)' : ''}</label>)}
          </div>
        </fieldset>
      )}
      <button type="button" className="small-action" onClick={() => setMore((m) => !m)} aria-expanded={more}>{more ? 'Ocultar opções' : 'Mais opções (concorrentes extras, assuntos, observações)'}</button>
      {more && (
        <div className="form-grid">
          <label className="field wide"><span>Outros concorrentes a incluir <span className="cell-sub">um por linha ou separados por vírgula</span></span><textarea rows={2} value={extra} onChange={(e) => setExtra(e.target.value)} /></label>
          <fieldset className="field wide"><legend>Assuntos a pesquisar sobre cada concorrente</legend>
            <div className="check-grid">{ASPECTS.map((a) => <label key={a.key} className="check"><input type="checkbox" checked={aspects.includes(a.key)} onChange={() => toggle(aspects, setAspects, a.key)} /> {a.label}</label>)}</div>
          </fieldset>
          <label className="field wide"><span>Observações para a pesquisa</span><textarea rows={2} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
        </div>
      )}
      {error && <div className="form-error" id="new-error" role="alert">{error}</div>}
      <div className="form-actions">
        <Link className="secondary" to="/mercado" style={{ textDecoration: 'none' }}>Cancelar</Link>
        <button className="cta" type="submit" disabled={busy}>{busy ? 'Preparando…' : 'Ver o que será pesquisado'}</button>
      </div>
    </form>
  );
}
